import clsx from 'clsx';
import { Cake, ChevronLeft, ChevronRight, Mail } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { addMonths, isoWeekday } from '@pz/core';
import { Avatar } from '../components/avatar';
import { Button, EmptyState, ErrorBox, IconButton, Input, Loading, PageHeader, Pill, Select, Tabs } from '../components/ui';
import { d, month as monthLabel, wd } from '../lib/format';
import { useQ, useTabParam } from '../lib/hooks';
import { ScrollTable } from '../components/virtual';
import { useMe } from '../lib/me';

/** Colour of an absence in the grid by its label (kind released for the team, or "absent"). */
const CELL: Record<string, string> = {
  vacation: 'bg-accent/75',
  training: 'bg-good/60',
  sick: 'bg-bad/60',
  child_sick: 'bg-bad/60',
};
const cellClass = (label: string) => CELL[label] ?? 'bg-graphite/45';

/**
 * Team: who is there when. For everybody in the company (not for external partners): approved absences
 * (by kind only where released, sickness always as "abwesend"), public holidays of each location, company
 * events, birthdays that people chose to show, and the projects of each person. No hours, no tasks.
 */
export function TeamPage() {
  const { t } = useTranslation();
  const { me, personId } = useMe();
  const [ym, setYm] = useState(me.today.slice(0, 7));
  const [tab, setTab] = useTabParam(['calendar', 'people'] as const);
  const [country, setCountry] = useState<'all' | 'DE' | 'TR'>('all');
  const [discipline, setDiscipline] = useState('');
  const [search, setSearch] = useState('');
  const q = useQ('leave.team', { month: ym });
  const data = q.data;
  const disciplines = useMemo(() => [...new Set((data?.persons ?? []).map((p) => p.discipline).filter(Boolean) as string[])].sort(), [data]);
  const persons = (data?.persons ?? []).filter(
    (p) =>
      (country === 'all' || p.country === country) &&
      (!discipline || p.discipline === discipline) &&
      (!search || p.name.toLowerCase().includes(search.toLowerCase())),
  );
  const shift = (n: number) => setYm(addMonths(`${ym}-01`, n).slice(0, 7));
  const labelOf = (label: string) => t(`week.absence.${label as 'vacation'}`);

  return (
    <div className="flex flex-col gap-4">
      <PageHeader title={t('team.title')} subtitle={t('team.subtitle')} />
      <div className="flex flex-wrap items-center gap-2 rounded-lg border border-line bg-sheet px-3 py-2">
        <IconButton label={t('common.previous')} icon={<ChevronLeft className="size-4" />} onClick={() => shift(-1)} />
        <span className="w-40 text-center font-semibold">{monthLabel(`${ym}-01`)}</span>
        <IconButton label={t('common.next')} icon={<ChevronRight className="size-4" />} onClick={() => shift(1)} />
        <Button size="sm" variant="ghost" onClick={() => setYm(me.today.slice(0, 7))}>
          {t('team.today')}
        </Button>
        <div className="ml-auto flex flex-wrap items-center gap-2">
          <Select className="w-44" value={country} onChange={(e) => setCountry(e.target.value as 'all')} aria-label={t('team.country')}>
            <option value="all">
              {t('team.country')}: {t('team.all')}
            </option>
            <option value="DE">{t('people.countries.DE')}</option>
            <option value="TR">{t('people.countries.TR')}</option>
          </Select>
          <Select className="w-52" value={discipline} onChange={(e) => setDiscipline(e.target.value)} aria-label={t('team.discipline')}>
            <option value="">
              {t('team.discipline')}: {t('team.all')}
            </option>
            {disciplines.map((x) => (
              <option key={x} value={x}>
                {x}
              </option>
            ))}
          </Select>
          <Input className="w-44" placeholder={t('team.search')} value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
      </div>
      <Tabs
        value={tab}
        onChange={setTab}
        tabs={[
          { id: 'calendar', label: t('team.calendar') },
          { id: 'people', label: t('team.people') },
        ]}
      />
      {q.isLoading && <Loading />}
      <ErrorBox error={q.error} />
      {data && data.events.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <span className="text-xs font-medium text-graphite">{t('team.events')}:</span>
          {data.events.map((e) => (
            <Pill key={e.id} tone="info" title={e.personIds.length ? t('leave.events.participantsCount', { count: e.personIds.length }) : t('leave.events.everybody')}>
              {e.from === e.to ? d(e.from) : `${d(e.from)} - ${d(e.to)}`} {e.title}
              {e.part === 'time' && e.startTime && e.endTime ? ` · ${e.startTime}–${e.endTime}` : e.part === 'am' || e.part === 'pm' ? ` · ${t(`leave.parts.${e.part}`)}` : ''}
              {e.country ? ` (${e.country})` : ''}
              {e.personIds.length > 0 ? ` · ${t('leave.events.participantsCount', { count: e.personIds.length })}` : ''}
            </Pill>
          ))}
        </div>
      )}
      {data && persons.length === 0 && <EmptyState>{t('common.noData')}</EmptyState>}
      {data && persons.length > 0 && tab === 'calendar' && (
        <>
          <ScrollTable label={t('team.title')}>
            <table className="border-collapse text-xs">
              <thead>
                <tr className="border-b border-line bg-sheet-2">
                  <th className="sticky left-0 z-10 min-w-48 bg-sheet-2 px-3 py-2 text-left font-medium text-graphite">{t('common.person')}</th>
                  {data.days.map((day) => {
                    const weekend = isoWeekday(day) >= 6;
                    const event = data.events.some((e) => e.from <= day && e.to >= day);
                    return (
                      <th key={day} className={clsx('w-7 min-w-7 px-0 py-1 text-center font-normal', weekend && 'text-faint', day === data.today && 'text-accent')}>
                        <div>{wd(day).slice(0, 2)}</div>
                        <div className={clsx('num font-semibold', event && 'underline decoration-info decoration-2 underline-offset-2')}>{Number(day.slice(8))}</div>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {persons.map((p) => (
                  <tr key={p.id} className={clsx('border-b border-line/60', p.id === personId && 'bg-accent/5')}>
                    <th className="sticky left-0 z-10 bg-sheet px-3 py-1.5 text-left font-normal">
                      <div className="flex items-center gap-2">
                        <Avatar personId={p.id} name={p.name} avatarAt={p.avatarAt} size={22} />
                        <div className="min-w-0">
                          <div className="truncate font-medium">{p.name}</div>
                          <div className="text-[11px] text-graphite">{[p.discipline, p.country].filter(Boolean).join(' · ')}</div>
                        </div>
                      </div>
                    </th>
                    {p.days.map((c, i) => {
                      const day = data.days[i];
                      const birthday = p.birthday && day.slice(5) === p.birthday;
                      const eventText = c.event
                        ? `${c.event.title}${c.event.part === 'time' && c.event.startTime && c.event.endTime ? ` ${c.event.startTime}–${c.event.endTime}` : c.event.part === 'am' || c.event.part === 'pm' ? ` (${t(`leave.parts.${c.event.part}`)})` : ''}`
                        : null;
                      const title = [c.holiday, c.absence ? labelOf(c.absence.label) : null, eventText, birthday ? t('team.birthday') : null].filter(Boolean).join(' · ');
                      return (
                        <td
                          key={day}
                          title={title || undefined}
                          className={clsx('relative h-8 w-7 border-l border-line/40 p-0', !c.workday && 'bg-sheet-2', c.holiday && 'hatch', day === data.today && 'outline outline-1 -outline-offset-1 outline-accent')}
                        >
                          {c.absence && (
                            <span
                              className={clsx(
                                'absolute inset-x-0.5 rounded-sm',
                                cellClass(c.absence.label),
                                c.absence.part === 'full' ? 'inset-y-1' : c.absence.part === 'pm' ? 'top-1/2 bottom-1' : c.absence.part === 'am' ? 'top-1 bottom-1/2' : 'inset-y-2.5',
                              )}
                            />
                          )}
                          {birthday && <Cake className="absolute top-0.5 right-0.5 size-3 text-warn" aria-label={t('team.birthday')} />}
                          {c.event && <span className="absolute inset-x-0.5 bottom-0.5 h-1 rounded-sm bg-info" aria-label={eventText ?? undefined} />}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </ScrollTable>
          <div className="flex flex-wrap items-center gap-4 text-xs text-graphite">
            <Legend className={cellClass('vacation')} label={labelOf('vacation')} />
            <Legend className={cellClass('training')} label={labelOf('training')} />
            <Legend className={cellClass('absent')} label={t('team.legendAbsent')} />
            <Legend className="hatch border border-line" label={t('team.legendHoliday')} />
            <Legend className="bg-sheet-2 border border-line" label={t('team.legendFree')} />
            <span className="flex items-center gap-1">
              <Cake className="size-3 text-warn" /> {t('team.birthday')}
            </span>
            <span className="flex items-center gap-1">
              <span className="inline-block h-1 w-3 rounded-sm bg-info" /> {t('team.legendEvent')}
            </span>
            <span>{t('team.privacy')}</span>
          </div>
        </>
      )}
      {data && persons.length > 0 && tab === 'people' && <p className="text-xs text-graphite">{t('team.leadHint')}</p>}
      {data && persons.length > 0 && tab === 'people' && (
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          {persons.map((p) => (
            <div key={p.id} className="flex flex-col gap-2 rounded-lg border border-line bg-sheet p-4">
              <div className="flex items-center gap-3">
                <Avatar personId={p.id} name={p.name} avatarAt={p.avatarAt} size={36} />
                <div className="min-w-0 flex-1">
                  <div className="truncate font-semibold">{p.name}</div>
                  <div className="text-xs text-graphite">{[p.discipline, p.country === 'TR' ? t('people.countries.TR') : t('people.countries.DE')].filter(Boolean).join(' · ')}</div>
                </div>
                <a href={`mailto:${p.email}`} className="rounded p-1.5 text-graphite hover:bg-sheet-2 hover:text-accent" title={t('team.mail')} aria-label={t('team.mail')}>
                  <Mail className="size-4" />
                </a>
              </div>
              <div className="text-sm">
                {p.today ? (
                  <Pill tone="warn">{t('team.until', { label: labelOf(p.today.label), date: d(p.today.until) })}</Pill>
                ) : (
                  <Pill tone="good">{t('team.present')}</Pill>
                )}
              </div>
              <div className="text-xs">
                <div className="mb-1 font-medium text-graphite">{t('team.projects')}</div>
                {p.projects.length === 0 && <span className="text-faint">{t('team.noProjects')}</span>}
                <div className="flex flex-wrap gap-1">
                  {p.projects.map((x) => (
                    <Pill key={x.number} tone={x.lead ? 'accent' : 'neutral'} title={x.lead ? t('team.lead') : undefined}>
                      <span className="num">{x.number}</span> {x.name}
                    </Pill>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Legend({ className, label }: { className: string; label: string }) {
  return (
    <span className="flex items-center gap-1">
      <span className={clsx('inline-block size-3 rounded-sm', className)} /> {label}
    </span>
  );
}
